import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    makeStyles,
    Button,
    Popper,
    Paper,
    Grow,
    MenuList,
    MenuItem,
    ClickAwayListener,
    Box,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { colors } from '../../../constants.js/colors';
import { KEY_SELECTED_CATEGORY } from '../../../constants.js/keys';
import { getCollectionsService } from '../../../services/collections';
import { extractCollectionRows, getChildCollections } from '../../../utils/collections-tree';

const useStyles = makeStyles((theme) => ({
    btn: {
        '& .MuiButton-label': { fontSize: 14 },
        '& .MuiButton-contained': { background: '#fff' },
    },
    menuList: {
        maxHeight: 300,
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { background: colors.primary.main },
    },
    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 2),
    },
    filterRowMobile: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2.5),
        backgroundColor: '#fff',
        borderBottom: `1px solid ${colors.gray.main}`,
    },
    filterGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1),
        width: '100%',
    },
    filterChipButton: {
        justifyContent: 'space-between',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        padding: theme.spacing(1.25, 1.5),
        minHeight: 44,
        border: `1px solid ${colors.gray.main}`,
        borderRadius: 10,
        backgroundColor: '#fff',
        color: colors.text.primary,
        fontSize: '0.875rem',
        fontWeight: 500,
        width: '100%',
        '& .MuiButton-endIcon': {
            marginLeft: theme.spacing(0.5),
        },
        '&:disabled': {
            color: colors.gray.alternative,
            backgroundColor: '#f7f7f7',
        },
    },
    clearFiltersButton: {
        width: '100%',
        justifyContent: 'center',
        textTransform: 'none',
        minHeight: 40,
        fontWeight: 500,
        color: colors.text.secondary,
        '&:disabled': {
            color: colors.gray.alternative,
        },
    },
    group: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: '8px',
    },
    iconLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 14,
        color: '#333',
    },
}));

const Filters = ({
    storeId,
    selectedCategories,
    setSelectedCategories,
    selectedSubcategory,
    setSelectedSubcategory,
    selectedStatus,
    setSelectedStatus,
    selectedOrder,
    setSelectedOrder,
    onClearFilters,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [openMenu, setOpenMenu] = useState(null);
    const [collectionData, setCollectionData] = useState([]);
    const [allCollectionsRows, setAllCollectionsRows] = useState([]);
    const [subcategoryData, setSubcategoryData] = useState([]);
    const [categorySel, setCategorySel] = useState(
        JSON.parse(localStorage.getItem(KEY_SELECTED_CATEGORY))
    );

    const anchorRefs = {
        categorie: useRef(null),
        subcategorie: useRef(null),
        status: useRef(null),
        order: useRef(null),
    };

    const statusOptions = [t('Product.filter.inactive'), t('Product.filter.active')];
    const orderOptions = [
        { value: null, label: t('Product.filter.orderDefault') },
        { value: 'asc', label: t('Product.filter.orderAz') },
        { value: 'desc', label: t('Product.filter.orderDz') },
    ];

    const getOrderLabel = () => {
        if (selectedOrder === 'asc') return t('Product.filter.orderAz');
        if (selectedOrder === 'desc') return t('Product.filter.orderDz');
        return t('Product.filter.orderDefault');
    };

    const hasActiveFilters =
        selectedStatus !== null ||
        selectedCategories ||
        selectedSubcategory ||
        selectedOrder;

    useEffect(() => {
        getCollectionsService(storeId)
            .then(({ data }) => {
                const rows = extractCollectionRows(data);
                setAllCollectionsRows(rows);
                setCollectionData(rows.filter((r) => !r.parent_id).sort((a, b) => Number(b.default) - Number(a.default)));
            })
            .catch(() => {
                setCollectionData([]);
                setAllCollectionsRows([]);
            });
    }, [storeId]);

    useEffect(() => {
        if (categorySel) setSelectedCategories(categorySel);
    }, [categorySel]);

    useEffect(() => {
        if (!selectedCategories || !selectedCategories.id) {
            setSubcategoryData([]);
            setSelectedSubcategory(null);
            return;
        }

        const directSubs = getChildCollections(selectedCategories);
        const fallbackSubs = allCollectionsRows.filter(
            (row) => String(row.parent_id) === String(selectedCategories.id)
        );
        const merged = [...(Array.isArray(directSubs) ? directSubs : []), ...fallbackSubs];
        const unique = merged.filter(
            (sub, index, arr) => arr.findIndex((x) => String(x.id) === String(sub.id)) === index
        );
        const sorted = unique.sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
        setSubcategoryData(sorted);
        if (
            selectedSubcategory &&
            !sorted.some((sub) => String(sub.id) === String(selectedSubcategory.id))
        ) {
            setSelectedSubcategory(null);
        }
    }, [selectedCategories, allCollectionsRows, selectedSubcategory, setSelectedSubcategory]);

    const handleSelect = (type, value, index = null) => {
        if (type === 'categorie') {
            setSelectedCategories(value);
            setCategorySel(value);
            localStorage.setItem(KEY_SELECTED_CATEGORY, JSON.stringify(value));
        } else if (type === 'subcategorie') {
            setSelectedSubcategory(value);
        } else if (type === 'status') {
            setSelectedStatus(index);
        } else if (type === 'order') {
            setSelectedOrder(value);
        }
        setOpenMenu(null);
    };

    const handleClear = () => {
        setSelectedStatus(null);
        setSelectedCategories('');
        setSelectedSubcategory(null);
        setSelectedOrder(null);
        localStorage.removeItem(KEY_SELECTED_CATEGORY);
        if (onClearFilters) {
            onClearFilters();
        }
    };

    const renderFilterButtons = (mobileLayout = false) => (
        <>
            <Button
                ref={anchorRefs.categorie}
                onClick={() => setOpenMenu(openMenu === 'categorie' ? null : 'categorie')}
                endIcon={<ExpandMoreIcon />}
                size="small"
                className={mobileLayout ? classes.filterChipButton : undefined}
            >
                {selectedCategories ? selectedCategories.name : t('Product.filter.categorie')}
            </Button>

            <Button
                ref={anchorRefs.subcategorie}
                onClick={() => setOpenMenu(openMenu === 'subcategorie' ? null : 'subcategorie')}
                endIcon={<ExpandMoreIcon />}
                size="small"
                disabled={!selectedCategories || subcategoryData.length === 0}
                className={mobileLayout ? classes.filterChipButton : undefined}
            >
                {selectedSubcategory ? selectedSubcategory.name : t('Product.filter.subcategory')}
            </Button>

            <Button
                ref={anchorRefs.status}
                onClick={() => setOpenMenu(openMenu === 'status' ? null : 'status')}
                endIcon={<ExpandMoreIcon />}
                size="small"
                className={mobileLayout ? classes.filterChipButton : undefined}
            >
                {selectedStatus !== null
                    ? statusOptions[selectedStatus]
                    : t('Product.filter.visibility')}
            </Button>

            <Button
                ref={anchorRefs.order}
                onClick={() => setOpenMenu(openMenu === 'order' ? null : 'order')}
                endIcon={<ExpandMoreIcon />}
                size="small"
                variant={mobileLayout ? 'outlined' : 'contained'}
                className={mobileLayout ? classes.filterChipButton : classes.btn}
            >
                {getOrderLabel()}
            </Button>
        </>
    );

    return (
        <Box className={isMobile ? classes.filterRowMobile : classes.filterRow}>
            {isMobile ? (
                <>
                    <Box className={classes.filterGrid}>{renderFilterButtons(true)}</Box>
                    <Button
                        size="small"
                        onClick={handleClear}
                        disabled={!hasActiveFilters}
                        className={classes.clearFiltersButton}
                    >
                        {t('Product.filter.empty')}
                    </Button>
                </>
            ) : (
                <Box className={classes.group}>
                    {renderFilterButtons(false)}
                    <Button
                        size="small"
                        onClick={handleClear}
                        disabled={!hasActiveFilters}
                    >
                        {t('Product.filter.empty')}
                    </Button>
                </Box>
            )}

            {/* Right Group: Order */}
            {!isMobile && <Box className={classes.group}></Box>}

            {/* Category Popper */}
            <Popper
                open={openMenu === 'categorie'}
                anchorEl={anchorRefs.categorie.current}
                transition
                disablePortal
                style={{ zIndex: 9999 }}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpenMenu(null)}>
                                <MenuList className={classes.menuList}>
                                    {collectionData.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            onClick={() => handleSelect('categorie', option)}
                                        >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            <Popper
                open={openMenu === 'subcategorie'}
                anchorEl={anchorRefs.subcategorie.current}
                transition
                disablePortal
                style={{ zIndex: 9999 }}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpenMenu(null)}>
                                <MenuList className={classes.menuList}>
                                    {subcategoryData.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            selected={
                                                selectedSubcategory &&
                                                String(option.id) === String(selectedSubcategory.id)
                                            }
                                            onClick={() => handleSelect('subcategorie', option)}
                                        >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            {/* Status Popper */}
            <Popper
                open={openMenu === 'status'}
                anchorEl={anchorRefs.status.current}
                transition
                disablePortal
                style={{ zIndex: 9999 }}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpenMenu(null)}>
                                <MenuList className={classes.menuList}>
                                    {statusOptions.map((option, index) => (
                                        <MenuItem
                                            key={option}
                                            selected={index === selectedStatus}
                                            onClick={() => handleSelect('status', option, index)}
                                        >
                                            {option}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            {/* Order Popper */}
            <Popper
                open={openMenu === 'order'}
                anchorEl={anchorRefs.order.current}
                transition
                disablePortal
                style={{ zIndex: 9999 }}
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpenMenu(null)}>
                                <MenuList className={classes.menuList}>
                                    {orderOptions.map((option) => (
                                        <MenuItem
                                            key={option.label}
                                            selected={selectedOrder === option.value}
                                            onClick={() => handleSelect('order', option.value)}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Box>
    );
};

const mapStateToProps = ({ auth }) => ({
    storeId: auth.storeId,
});

export default connect(mapStateToProps)(Filters);
